<template>
	<BlockVisibilityObserver title="MAIN">
		<section ref="hero" class="kova-v2-hero">
			<p class="kova-v2-hero__ribbon">{{ ribbon }}</p>

			<div class="kova-v2-hero__panel">
				<!-- Mobile: blurred ellipses behind the green panel (6357:60813 / 6594:13344 /
				     6357:60800). Desktop draws its own set inside the product card. -->
				<div aria-hidden="true" class="kova-v2-hero__glows kova-v2-hero__glows_mobile">
					<span class="kova-v2-hero__glow kova-v2-hero__glow_m2976" />
					<span class="kova-v2-hero__glow kova-v2-hero__glow_m2974" :style="{ top: `${art.glows.mobile}px` }" />
					<span class="kova-v2-hero__glow kova-v2-hero__glow_m2975" />
				</div>

				<div class="kova-v2-hero__header">
					<span aria-hidden="true" class="kova-v2-hero__header-bg"><span /></span>
					<picture class="kova-v2-hero__logo">
						<source height="51" media="(min-width: 1024px)" :srcset="logoDark" width="190" />
						<img alt="Kova" height="34" :src="logoLime" width="124" />
					</picture>
					<LandingKovaV2Button
						class="kova-v2-hero__header-cta"
						:label="headerCtaLabel"
						variant="header"
						@click="onCta('header_eligible')"
					/>
				</div>

				<div class="kova-v2-hero__body">
					<!-- Desktop: the green product card. Mobile: a transparent layer over the
					     whole panel, so the product art and the delivery seal are placed in
					     the panel's own coordinates (6594:13331, 6357:60755). -->
					<div class="kova-v2-hero__card">
						<div aria-hidden="true" class="kova-v2-hero__glows kova-v2-hero__glows_desktop">
							<span class="kova-v2-hero__glow kova-v2-hero__glow_d2976" :style="{ top: `${art.glows.desktop[0]}px` }" />
							<span class="kova-v2-hero__glow kova-v2-hero__glow_d2974" :style="{ top: `${art.glows.desktop[1]}px` }" />
							<span class="kova-v2-hero__glow kova-v2-hero__glow_d2975" :style="{ top: `${art.glows.desktop[2]}px` }" />
						</div>

						<p class="kova-v2-hero__stock">
							<span aria-hidden="true" class="kova-v2-hero__stock-dot" />
							<span>{{ inStock }}</span>
						</p>

						<picture class="kova-v2-hero__art" :style="artStyle">
							<source
								:height="art.desktop.height"
								media="(min-width: 1024px)"
								:srcset="art.desktop.src"
								:width="art.desktop.width"
							/>
							<img
								alt=""
								fetchpriority="high"
								:height="art.mobile.height"
								loading="eager"
								:src="art.mobile.src"
								:width="art.mobile.width"
							/>
						</picture>

						<img alt="Free delivery" class="kova-v2-hero__seal" height="300" loading="eager" :src="seal" width="300" />
					</div>

					<div class="kova-v2-hero__rating">
						<span class="kova-v2-hero__rating-row">
							<LandingKovaStars class="kova-v2-hero__stars" />
							<span class="kova-v2-hero__rating-score" v-text="`${rating.score}  ${rating.label}`" />
						</span>
						<span class="kova-v2-hero__rating-caption">{{ rating.caption }}</span>
					</div>

					<h1 class="kova-v2-hero__title">{{ title }}</h1>

					<div class="kova-v2-hero__price">
						<p class="kova-v2-hero__price-label">{{ price.label }}</p>
						<p class="kova-v2-hero__price-row">
							<span class="kova-v2-hero__price-now">{{ price.now }}</span>
							<span class="kova-v2-hero__price-side">
								<!-- The strike-through is visual only; screen readers get the words. -->
								<s class="kova-v2-hero__price-was">
									<span class="sr-only" v-text="', regular price '" />
									<span v-text="price.was" />
									<span class="sr-only" v-text="','" />
								</s>
								<span class="kova-v2-hero__price-period">{{ price.period }}</span>
							</span>
						</p>
					</div>

					<p class="kova-v2-hero__description">{{ description }}</p>

					<ul class="kova-v2-hero__features">
						<li v-for="feature in features" :key="feature" class="kova-v2-hero__feature">
							<span aria-hidden="true" class="kova-v2-hero__feature-check"><InlineSvg :src="check" /></span>
							<span>{{ feature }}</span>
						</li>
					</ul>

					<div ref="ctaSlot" class="kova-v2-hero__cta">
						<LandingKovaV2Button :label="headerCtaLabel" variant="hero" @click="onCta('main_eligible')" />
					</div>
				</div>
			</div>
			<LandingKovaV2Button
				v-show="showFloatingCta && !bannerVisible"
				class="kova-v2-hero__floating-cta"
				:label="headerCtaLabel"
				:tone="floatingTone"
				variant="floating"
				@click="onFloatingCta"
			/>
		</section>
	</BlockVisibilityObserver>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useCookieBannerVisible } from '@/composables/useCookieBannerVisible'
import { useOnboardingStepEvents } from '@/composables/useOnboardingStepEvents'

import BlockVisibilityObserver from '@/components/BlockVisibilityObserver.vue'
import LandingKovaStars from '@/modules/Landing/components/LandingKovaStars.vue'
import LandingKovaV2Button from '@/modules/Landing/components/v2/LandingKovaV2Button.vue'
import { KovaV2CtaEntryPoint, LandingComponentProps } from '@/modules/Landing/instances/@types/landing-types'

import check from '@/assets/kova/landing/check.svg'
import heroSemaglutideDesktop from '@/assets/kova/landing/v2/hero-semaglutide-desktop.webp'
import heroSemaglutideMobile from '@/assets/kova/landing/v2/hero-semaglutide-mobile.webp'
import heroTirzepatideDesktop from '@/assets/kova/landing/v2/hero-tirzepatide-desktop.webp'
import heroTirzepatideMobile from '@/assets/kova/landing/v2/hero-tirzepatide-mobile.webp'
import heroUniversalDesktop from '@/assets/kova/landing/hero-vials-universal-desktop.png'
import heroUniversalMobile from '@/assets/kova/landing/hero-vials-universal-mobile.png'
import logoDark from '@/assets/kova/landing/logo-kova-dark.svg'
import logoLime from '@/assets/kova/logo-light-green.svg'
import seal from '@/assets/kova/landing/promo-badge-free-delivery.png'

defineOptions({ name: 'LandingKovaV2Hero' })

const props = defineProps<LandingComponentProps.KovaV2Hero>()

const emit = defineEmits<{
	progressLoaderCompleted: [entryPoint: KovaV2CtaEntryPoint, dataString?: string, blockName?: string]
}>()
const { jetButtonClick } = useOnboardingStepEvents()

/** Width of the desktop product card in the 1440 frame (6594:12414). */
const DESKTOP_CARD_WIDTH = 652

type Shot = { src: string; width: number; height: number; top: number }

// Product art per variant. Mobile boxes are placed in the 390 green panel (236px down,
// bleeding 34/35/42px past its right edge, clipped by it); desktop boxes sit in the 652x680
// card. The sema/tirz files are transparent composites of the Figma layers (vial +
// radial-masked ground shadow), so the panel glows show through. `glows` are the ellipse
// tops (mobile 6594:13344; desktop 2976 / 2974 / 2975 in the card). Nodes: universal
// 6594:13331 / 6594:12437, semaglutide 6594:13361 / 6594:12070 (551x589 at (50,-35), cut by
// the card top), tirzepatide 6594:13369 / 6566:34741 (489x489, centre +13px, 16 down).
const HERO_ART: Record<
	LandingComponentProps.KovaV2Hero['product'],
	{
		mobile: Shot & { right: number }
		desktop: Shot & { left: number }
		glows: { mobile: number; desktop: [number, number, number] }
	}
> = {
	universal: {
		mobile: { src: heroUniversalMobile, width: 192, height: 264, top: 236, right: -34 },
		desktop: { src: heroUniversalDesktop, width: 480, height: 548, top: 0, left: 86 },
		glows: { mobile: 140, desktop: [-62, 11, 533] },
	},
	semaglutide: {
		mobile: { src: heroSemaglutideMobile, width: 204, height: 288, top: 236, right: -35 },
		desktop: { src: heroSemaglutideDesktop, width: 551, height: 589, top: -35, left: 50 },
		glows: { mobile: 112, desktop: [-82, -9, 553] },
	},
	tirzepatide: {
		mobile: { src: heroTirzepatideMobile, width: 208, height: 272, top: 236, right: -42 },
		desktop: { src: heroTirzepatideDesktop, width: 489, height: 489, top: 16, left: 94.5 },
		glows: { mobile: 112, desktop: [-62, 11, 573] },
	},
}

const art = computed(() => HERO_ART[props.product])

// The art box geometry as CSS custom properties: mobile is anchored to the panel's right
// edge, desktop is kept relative to the card centre (Figma's card is 652 wide), so the
// composition holds when the card narrows between 1024 and 1440.
const artStyle = computed(() => {
	const { desktop, mobile } = art.value
	return {
		'--art-m-w': `${mobile.width}px`,
		'--art-m-h': `${mobile.height}px`,
		'--art-m-top': `${mobile.top}px`,
		'--art-m-right': `${mobile.right}px`,
		'--art-d-w': `${desktop.width}px`,
		'--art-d-h': `${desktop.height}px`,
		'--art-d-top': `${desktop.top}px`,
		'--art-d-shift': `${desktop.left + desktop.width / 2 - DESKTOP_CARD_WIDTH / 2}px`,
	}
})

// Keep an eligibility action visible outside the inline button's viewport.
// Consent uses its existing visibility signal; no banner measurements or layout changes.
const ctaSlot = ref<HTMLElement | null>(null)
const hero = ref<HTMLElement | null>(null)
const showFloatingCta = ref(false)
const floatingTone = ref<'hero' | 'body'>('hero')
const { bannerVisible } = useCookieBannerVisible()
let ctaObserver: IntersectionObserver | null = null

onMounted(() => {
	ctaObserver = new IntersectionObserver(
		(entries) => {
			for (const entry of entries) {
				if (entry.target === ctaSlot.value) {
					showFloatingCta.value = entry.intersectionRatio < 1
				} else {
					// The hero crosses the viewport even when a jump skips the inline CTA entirely.
					floatingTone.value = entry.boundingClientRect.bottom <= 0 ? 'body' : 'hero'
				}
			}
		},
		{ threshold: [0, 1] },
	)
	if (ctaSlot.value) ctaObserver.observe(ctaSlot.value)
	if (hero.value) ctaObserver.observe(hero.value)
})
onBeforeUnmount(() => ctaObserver?.disconnect())

// Like LandingKovaHeader, the header tracks its click without a block name.
// LandingView tracks section CTAs and owns completion events and navigation.
function onCta(entryPoint: KovaV2CtaEntryPoint) {
	if (entryPoint === 'header_eligible') jetButtonClick('CHECK_MY_ELIGIBILITY')
	emit('progressLoaderCompleted', entryPoint)
}

function onFloatingCta() {
	// Attribute the click to the block occupying the most viewport space right now,
	// including when the user scrolls back up. Reuse the block-view names.
	const blocks = hero.value?.closest('.landing')?.querySelectorAll<HTMLElement>('[data-block-name]') ?? []
	let blockName = 'MAIN'
	let largestVisibleHeight = 0
	for (const block of blocks) {
		const rect = block.getBoundingClientRect()
		const visibleHeight = Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0)
		if (visibleHeight > largestVisibleHeight) {
			largestVisibleHeight = visibleHeight
			blockName = block.dataset.blockName!
		}
	}
	emit('progressLoaderCompleted', 'main_eligible', undefined, blockName)
}
</script>

<style lang="scss" scoped>
// ── Mobile 390 (6357:60725) ─────────────────────────────────────────────────────────
.kova-v2-hero {
	font-family: var(--kova-lp-font);
}

// 6357:60726 — 390x52, 16px Regular / 20 centred, 16px block padding.
.kova-v2-hero__ribbon {
	margin: 0;
	padding: var(--size-16) var(--size-10);

	font-size: var(--size-16);
	line-height: var(--size-20);
	text-align: center;

	color: var(--Fill-White);
	background: var(--kova-v2-orange);
}

// 6357:60728 — the green panel, 32px bottom corners, art clipped at its edges.
.kova-v2-hero__panel {
	position: relative;
	overflow: hidden;
	isolation: isolate;

	border-end-start-radius: var(--size-32);
	border-end-end-radius: var(--size-32);
	background: var(--kova-lp-hero-panel);
}

.kova-v2-hero__glows {
	position: absolute;
	inset: 0;
	z-index: 0;
	pointer-events: none;
}

// Figma's blurred ellipses as CSS: the node box, the fill, and the layer blur as
// `filter: blur()` — CSS blur takes the Gaussian standard deviation, the same number
// Figma exports as `stdDeviation` (half its layer-blur radius). The rotated nodes
// (2976 / 2975) are drawn in their turned, landscape box.
.kova-v2-hero__glow {
	position: absolute;
	border-radius: 50%;
}

// 6357:60813 — 664x216 at (-145,-44), #06392B, blur 200.
.kova-v2-hero__glow_m2976 {
	left: -145px;
	top: -44px;
	z-index: 1;
	width: 664px;
	height: 216px;

	background: var(--kova-lp-hero-shade);
	filter: blur(100px);
}

// 6594:13344 — 400x480 at (80, art.glows.mobile), #2E9277, blur 160.
.kova-v2-hero__glow_m2974 {
	left: 80px;
	width: 400px;
	height: 480px;

	background: var(--kova-lp-hero-glow);
	filter: blur(80px);
}

// 6357:60800 — 664x209, 20px above the CTA group, i.e. 105px past the panel bottom.
.kova-v2-hero__glow_m2975 {
	left: -145px;
	bottom: -105px;
	width: 664px;
	height: 209px;

	background: var(--kova-lp-hero-shade);
	filter: blur(100px);
}

.kova-v2-hero__glows_desktop {
	display: none;
}

// 6357:60729 — logo row: 20 top, centred 121x28 (the SVG carries ~2px of bleed).
.kova-v2-hero__header {
	position: relative;
	z-index: 3;

	display: flex;
	justify-content: center;
	padding: var(--size-20) var(--size-32) 0;
}

.kova-v2-hero__header-bg,
.kova-v2-hero__header-cta {
	display: none;
}

// The lime logo is the shared `logo-light-green.svg` (the funnel header's), whose box
// carries ~2.5px of bleed round the 121x28 mark — Figma inset -9% 0 -9% -2.08%.
.kova-v2-hero__logo {
	display: block;
	width: 121.26px;
	height: var(--size-28);

	img {
		display: block;
		width: 124px;
		max-width: none;
		height: 34px; // the SVG's own 124x34 box — fixed like the mark, not rem-scaled
		margin: -2.52px 0 0 -2.52px;
	}
}

.kova-v2-hero__body {
	display: flex;
	flex-direction: column;
}

// Mobile: the card is a transparent full-panel layer — no stacking context of its own,
// so the art (z 1) stays under the copy (z 2) and the seal (z 4) over the rating pill.
.kova-v2-hero__card {
	position: absolute;
	inset: 0;
	pointer-events: none;
}

.kova-v2-hero__stock {
	display: none;
}

// 6594:13331 — art box anchored 236px down the panel, bleeding past its right edge.
.kova-v2-hero__art {
	position: absolute;
	top: var(--art-m-top);
	right: var(--art-m-right);
	z-index: 1;

	display: block;
	width: var(--art-m-w);
	height: var(--art-m-h);

	img {
		display: block;
		width: 100%;
		max-width: none;
		height: 100%;
	}
}

// 6357:60755 — "Free delivery" seal, 100px box hanging 12px past the pill's right edge;
// the shipped PNG is trimmed to the seal, sized to its measured 99px diameter. In rem
// (51 / 13 / 99px at 16px) so it keeps its place on the pill when the root font shrinks
// below 376px (style.scss), like the header around it.
.kova-v2-hero__seal {
	position: absolute;
	top: 3.1875rem;
	right: 0.8125rem;
	z-index: 4;

	display: block;
	width: 6.1875rem;
	height: 6.1875rem;
}

// 6357:60745 — white pill, 20/8 padding, rows 2px apart, stretched to 366px (8 + 16 insets).
.kova-v2-hero__rating {
	position: relative;
	z-index: 2;

	display: flex;
	flex-direction: column;
	gap: var(--size-2);
	margin: var(--size-24) var(--size-24) 0 var(--size-8);
	padding: var(--size-8) var(--size-20);

	border-radius: var(--size-16);
	background: var(--Fill-White);
}

.kova-v2-hero__rating-row {
	display: flex;
	align-items: center;
	gap: var(--size-8);
}

// The shared two-tone rating row: 88x15 here (6357:60747), 93x16 on desktop.
.kova-v2-hero__stars {
	height: 15px; // 6357:60747 — exact size not in design system
}

.kova-v2-hero__rating-score {
	font-size: var(--size-14);
	font-weight: 600;
	line-height: var(--size-20); // 14px × 1.4 = 19.60px — exact size not in design system
	white-space: pre; // "4.8  Excellent!" — two spaces in 6357:60753

	color: var(--kova-lp-ink);
}

.kova-v2-hero__rating-caption {
	font-size: var(--size-14);
	line-height: var(--size-20); // 14px × 1.4 = 19.60px — exact size not in design system

	color: var(--kova-v2-text-secondary);
}

// 6357:60758 — 24 Medium / 1.12, 16px gutters; 32px below the pill (12 + 20).
.kova-v2-hero__title {
	position: relative;
	z-index: 2;

	margin: var(--size-32) 0 0;
	padding-inline: var(--size-16);

	font-size: var(--size-24);
	font-weight: 500;
	line-height: var(--size-26); // 24px × 1.12 = 26.88px — exact size not in design system
	letter-spacing: -0.48px;

	color: var(--Fill-White);
}

// 6357:60759 / 60760 — price group 235 wide in a 210px column (16 left, 164 right).
// The row and the chips wrap when the text is enlarged (WCAG 1.4.4): at the drawn size
// nothing wraps, at 200% the struck price moves under the figure instead of being cut
// by the panel's overflow.
.kova-v2-hero__price {
	position: relative;
	z-index: 2;

	display: flex;
	flex-direction: column;
	gap: var(--size-8);
	width: 235px;
	max-width: calc(100% - var(--size-32));
	margin: var(--size-20) 0 0 var(--size-16);
	padding-bottom: var(--size-4);
}

.kova-v2-hero__price-label {
	margin: 0;

	font-size: var(--size-18);
	font-weight: 500;
	line-height: var(--size-20);
	letter-spacing: -0.36px;

	color: var(--kova-lp-hero-accent);
}

.kova-v2-hero__price-row {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: var(--size-8);
	margin: 0;
}

// Cap-height box, as in Figma (text-box-trim): the figure sits flush with its column.
.kova-v2-hero__price-now {
	font-size: var(--size-56);
	font-weight: 600;
	line-height: var(--size-56);
	letter-spacing: -1.12px;
	white-space: nowrap;
	text-box: trim-both cap alphabetic;

	color: var(--kova-lp-hero-accent);
}

.kova-v2-hero__price-side {
	flex-shrink: 0;
	display: flex;
	flex-direction: column;
	justify-content: center;
	gap: var(--size-4);
	min-height: var(--size-48); // Closest to 46px in Figma 6357:60764.
	font-weight: 500;
}

.kova-v2-hero__price-was {
	font-size: var(--size-20);
	line-height: var(--size-20);

	color: var(--Text-White-60);
}

.kova-v2-hero__price-period {
	font-size: var(--size-18);
	line-height: var(--size-20);
	letter-spacing: -0.36px;

	color: var(--kova-lp-hero-accent);
}

// 6357:60767 — 14 / 1.28, wraps at 210px (164px right inset of 6357:60759).
.kova-v2-hero__description {
	position: relative;
	z-index: 2;

	margin: var(--size-20) 164px 0 var(--size-16);

	font-size: var(--size-14);
	line-height: var(--size-18); // 14px × 1.28 = 17.92px — exact size not in design system
	letter-spacing: -0.056px;

	color: var(--Fill-White);
}

// 6357:60783 — glass chips, wrapping in 314px (16 left, 60 right).
.kova-v2-hero__features {
	position: relative;
	z-index: 2;

	display: flex;
	flex-wrap: wrap;
	gap: var(--size-8);
	margin: var(--size-20) var(--size-60) 0 var(--size-16);
	padding: 0;

	list-style: none;
}

.kova-v2-hero__feature {
	display: flex;
	align-items: center;
	gap: var(--size-6); // 6357:60793
	padding: calc(var(--size-8) - 1px); // 8 in Figma, whose stroke sits inside the box — minus the 1px border

	min-width: 0;

	font-size: var(--size-14);
	font-weight: 500;
	line-height: var(--size-20); // 14px × 1.4 = 19.60px — exact size not in design system
	letter-spacing: -0.28px;

	border: 1px solid var(--kova-lp-hero-chip-border);
	border-radius: var(--size-12);
	color: var(--kova-lp-hero-chip-ink);
	background: var(--kova-lp-hero-chip-bg);
}

// 6357:60794 — the landing's check glyph (`check.svg`, as in LandingKovaHero), 11.81 wide
// in its 14px box; Figma stores it upside down and flips it back.
.kova-v2-hero__feature-check {
	display: flex;
	flex-shrink: 0;
	align-items: center;
	justify-content: center;
	width: var(--size-14);
	height: var(--size-14);

	color: var(--kova-lp-hero-chip-ink);

	:deep(svg) {
		display: block;
		width: 11.81px;
		height: auto;
		transform: scaleY(-1);
	}
}

// 6357:60799: the inline action remains in the normal page flow.
.kova-v2-hero__cta {
	position: relative;
	z-index: 2;
	display: grid;
	margin-top: var(--size-20);
	padding: var(--size-12) var(--size-20) var(--size-24);
}

// 6547:4967: floating CTA, below the existing consent banner's stacking level.
.kova-v2-hero__floating-cta {
	position: fixed;
	z-index: 7;
	left: var(--size-16);
	right: var(--size-16);
	bottom: calc(var(--size-12) + env(safe-area-inset-bottom, 0px));
	width: calc(100% - var(--size-32));

	@media (min-width: 1024px) {
		left: 50%;
		right: auto;
		width: auto;
		transform: translateX(-50%);
	}
}

// ── Desktop 1440 (6547:16808) ──────────────────────────────────────────────────────
@media (min-width: 1024px) {
	// 6547:16810 — 18px Medium.
	.kova-v2-hero__ribbon {
		position: relative;
		z-index: 2;

		font-size: var(--size-18);
		font-weight: 500;
	}

	.kova-v2-hero__panel {
		overflow: visible;

		border-radius: 0;
		background: none;
	}

	.kova-v2-hero__glows_mobile {
		display: none;
	}

	// 6547:16812 — 46px gutters, 24 top, 32 bottom; logo left, CTA right.
	.kova-v2-hero__header {
		// Let the negative-z background sit behind the product card in the panel's stack.
		z-index: auto;

		justify-content: space-between;
		align-items: center;
		padding: var(--size-24) var(--size-48) var(--size-32); // Closest to 46px sides in Figma 6547:16808.
	}

	// 6547:16813 — translucent white blob, 1537x226 at (-49,-69): white @70% in Figma's
	// shape (a bottom edge that rises 34px towards the middle), layer blur 94. The shape is
	// clipped on the inner span; the blur sits on the outer one, so it is not clipped away.
	.kova-v2-hero__header-bg {
		position: absolute;
		left: -49px;
		top: -69px;
		z-index: -1;

		display: block;
		width: 1537px;
		height: 226px;

		filter: blur(47px);
		pointer-events: none;

		> span {
			display: block;
			width: 100%;
			height: 100%;

			background: var(--kova-v2-header-glass);
			clip-path: path('M0 0H1537V226C1537 226 1068.62 191.758 768.5 191.758C468.382 191.758 0 226 0 226Z');
		}
	}

	.kova-v2-hero__header-cta {
		display: inline-flex;
	}

	.kova-v2-hero__logo {
		width: 190px;
		height: var(--size-52);

		img {
			width: 189.67px;
			height: 50.53px;
			margin: 0.61px 0 0; // Figma inset 1.18% 0.18% 1.65% 0
		}
	}

	// 6547:16830 — copy column 580, card 652, 48 apart, 80px gutters, 62 below.
	.kova-v2-hero__body {
		display: grid;
		grid-template-columns: minmax(0, 580fr) minmax(0, 652fr);
		grid-template-rows: repeat(5, auto);
		column-gap: var(--size-48);
		align-items: start;
		max-width: 1440px;
		margin-inline: auto;
		padding: 0 var(--size-40) var(--size-64); // Closest to 62px bottom in Figma 6547:16808.
		box-sizing: border-box;
	}

	// 6594:12414 — green card, 36 radius, as tall as the copy column.
	.kova-v2-hero__card {
		position: relative;
		inset: auto;
		grid-column: 2;
		grid-row: 1 / -1;
		align-self: stretch;
		overflow: hidden;

		border-radius: var(--size-36);
		background: var(--kova-lp-hero-panel);
		pointer-events: auto;
	}

	.kova-v2-hero__glows_desktop {
		display: block;
	}

	// 6594:12415 / 12416 / 12417 — tops per variant in `art.glows.desktop`; blur 301.8.
	.kova-v2-hero__glow_d2976 {
		left: calc(50% - 332px);
		width: 664px;
		height: 216px;

		background: var(--kova-lp-hero-shade);
		filter: blur(150.9px);
	}

	.kova-v2-hero__glow_d2974 {
		left: calc(50% + 17px - 209px);
		width: 418px;
		height: 664px;

		background: var(--kova-lp-hero-glow);
		filter: blur(150.9px);
	}

	.kova-v2-hero__glow_d2975 {
		left: calc(50% - 332px);
		width: 664px;
		height: 209px;

		background: var(--kova-lp-hero-shade);
		filter: blur(150.9px);
	}

	// 6594:12434 — "In-Stock" pill at (32,32).
	.kova-v2-hero__stock {
		position: absolute;
		left: var(--size-32);
		top: var(--size-32);
		z-index: 2;

		display: flex;
		align-items: center;
		gap: var(--size-10);
		margin: 0;
		padding: calc(var(--size-10) - 1px) calc(var(--size-20) - 1px); // 10/20 minus the inside 1px stroke

		font-size: var(--size-24);
		line-height: var(--size-36);

		border: 1px solid var(--kova-lp-hero-badge);
		border-radius: var(--size-100);
		color: var(--kova-lp-hero-badge);
	}

	// 6594:12435 — 10px lime dot.
	.kova-v2-hero__stock-dot {
		display: block;
		width: var(--size-10);
		height: var(--size-10);

		border-radius: 50%;
		background: var(--kova-lp-hero-accent);
	}

	.kova-v2-hero__art {
		top: var(--art-d-top);
		left: calc(50% + var(--art-d-shift));
		right: auto;

		width: var(--art-d-w);
		height: var(--art-d-h);

		transform: translateX(-50%);
	}

	// 6594:12491 — 180px seal 32px in from the card's top-right corner.
	.kova-v2-hero__seal {
		top: var(--size-32);
		right: var(--size-32);

		width: 180px;
		height: 180px;
	}

	// 6547:16834 — one-row white pill, 16/12 padding.
	.kova-v2-hero__rating {
		grid-column: 1;
		grid-row: 1;
		justify-self: start;

		flex-direction: row;
		flex-wrap: wrap;
		align-items: center;
		gap: var(--size-8);
		margin: 0;
		padding: var(--size-12) var(--size-16);
	}

	.kova-v2-hero__stars {
		height: var(--size-16);
	}

	.kova-v2-hero__rating-score {
		font-size: var(--size-16);
		line-height: var(--size-22); // 16px × 1.4 = 22.40px — exact size not in design system
	}

	.kova-v2-hero__rating-caption {
		font-size: var(--size-16);
		line-height: var(--size-22); // 16px × 1.4 = 22.40px — exact size not in design system
		color: var(--kova-lp-ink-70);
	}

	// 6547:16849 — 52 / 1.12, 20px under the pill.
	.kova-v2-hero__title {
		grid-column: 1;
		grid-row: 2;

		margin: var(--size-20) 0 0;
		padding: 0;

		font-size: var(--size-52);
		line-height: var(--size-60); // 52px × 1.12 = 58.24px — exact size not in design system
		letter-spacing: -1.04px;

		color: var(--kova-lp-ink);
	}

	// 6547:16850 — 28 under the title. Figma's group is 339 wide, which the $199 figure
	// plus its 124px side column overruns by a pixel or two in the browser; the group takes
	// the column width instead so the wrapping price row (for large text) stays on one line.
	.kova-v2-hero__price {
		grid-column: 1;
		grid-row: 3;

		gap: var(--size-20);
		width: auto;
		max-width: 100%;
		margin: var(--size-28) 0 0;
		padding: 0;
	}

	.kova-v2-hero__price-label {
		font-size: var(--size-24);
		line-height: var(--size-26); // 24px × 1.1 = 26.40px — exact size not in design system
		letter-spacing: -0.48px;

		color: var(--kova-lp-brand);
	}

	.kova-v2-hero__price-row {
		align-items: flex-end;
		gap: var(--size-16);
	}

	// 6547:16853 — the 92px figure in a 4px padded box.
	.kova-v2-hero__price-now {
		padding-block: var(--size-4);

		font-size: var(--size-96); // Closest to 92px in Figma 6547:16854.
		line-height: var(--size-100); // Closest to 92px × 1.1 in Figma 6547:16854.
		letter-spacing: -1.84px;

		color: var(--kova-lp-highlight);
	}

	.kova-v2-hero__price-side {
		width: 124px;
		min-height: 0;
	}

	.kova-v2-hero__price-was {
		font-size: var(--size-34);
		line-height: var(--size-48); // 34px × 1.4 = 47.60px — exact size not in design system

		color: var(--kova-lp-muted);
	}

	.kova-v2-hero__price-period {
		font-size: var(--size-24);
		line-height: var(--size-26); // 24px × 1.1 = 26.40px — exact size not in design system
		letter-spacing: -0.48px;

		color: var(--kova-lp-brand);
	}

	// 6547:16858 — 24 Medium / 1.48 green.
	.kova-v2-hero__description {
		grid-column: 1;
		grid-row: 4;

		margin: var(--size-28) 0 0;

		font-size: var(--size-24);
		font-weight: 500;
		line-height: var(--size-36); // 24px × 1.48 = 35.52px — exact size not in design system
		letter-spacing: 0;

		color: var(--kova-lp-brand);
	}

	// 6594:12493 — the chips sit inside the card, 32px from its bottom-left corner.
	.kova-v2-hero__features {
		grid-column: 2;
		grid-row: 1 / -1;
		align-self: end;

		gap: var(--size-6);
		max-width: 601px;
		margin: 0 var(--size-20) var(--size-32) var(--size-32);
	}

	.kova-v2-hero__feature {
		padding: calc(var(--size-12) - 1px) calc(var(--size-14) - 1px); // 12/14 minus the inside 1px stroke

		font-size: var(--size-16);
		line-height: var(--size-22); // 16px × 1.4 = 22.40px — exact size not in design system
		letter-spacing: -0.16px;

		border-radius: var(--size-14);
	}

	// 6547:16860 — 40 under the description; the button hugs its label.
	.kova-v2-hero__cta {
		grid-column: 1;
		grid-row: 5;

		display: flex;
		align-items: flex-start;
		margin-top: var(--size-40);
		padding: 0;
	}
}

@media (min-width: 1440px) {
	.kova-v2-hero__body {
		padding-inline: var(--size-80);
	}
}
</style>
